import { lazy } from "react"
import { Navigate, type RouteObject } from "react-router"
import { EmbedPage } from "@atomic/app-shell"
import { LazyRoute } from "./layouts"
import { nucleusWorkflowsEnabled } from "./configs/nucleusWorkflows"
// Eager on purpose: the error view must not depend on a dynamic import that
// could itself fail (e.g. stale chunk after a deploy).
import { RouteErrorView } from "./components/RouteErrorView"
import { TrackerRedirect } from "./components/TrackerRedirect"
// Tiny constants (no component code) driving the pinned Demand routes below.
import {
  DEMAND_BASE_PATH,
  DEMAND_REPORT_SLUG,
} from "./containers/ReportingView/constants"
// Where the Replenishment page mounts — constants, no component code.
import { REPLENISHMENT_PAGES } from "./containers/ReplenishmentView/pages"

// PERF: non-default routes stay lazy — smaller initial bundle.
const MappingDetailView = lazy(() =>
  import("./containers/MappingDetailView").then((m) => ({ default: m.MappingDetailView })),
)
const DataSourceDetailView = lazy(() =>
  import("./containers/DataSourceDetailView").then((m) => ({ default: m.DataSourceDetailView })),
)
const RunHistoryView = lazy(() =>
  import("./containers/RunHistoryView").then((m) => ({ default: m.RunHistoryView })),
)
const ReportingView = lazy(() =>
  import("./containers/ReportingView").then((m) => ({ default: m.ReportingView })),
)
const BalanceWalkView = lazy(() =>
  import("./containers/BalanceWalkView").then((m) => ({ default: m.BalanceWalkView })),
)
const TrackerView = lazy(() =>
  import("./containers/TrackerView").then((m) => ({ default: m.TrackerView })),
)
const AllocationEngineView = lazy(() =>
  import("./containers/AllocationEngineView").then((m) => ({
    default: m.AllocationEngineView,
  })),
)
const ReplenishmentView = lazy(() =>
  import("./containers/ReplenishmentView").then((m) => ({
    default: m.ReplenishmentView,
  })),
)
const DemandReportingView = lazy(() =>
  import("./containers/DemandReportingView").then((m) => ({
    default: m.DemandReportingView,
  })),
)
const MasterDataView = lazy(() =>
  import("./containers/MasterDataView").then((m) => ({ default: m.MasterDataView })),
)
const TicketsView = lazy(() =>
  import("./containers/TicketsView/TicketsView").then((m) => ({
    default: m.TicketsView,
  })),
)
const BomExplorerView = lazy(() =>
  import("./containers/BomExplorerView").then((m) => ({ default: m.BomExplorerView })),
)
const NucleusWorkflowsView = lazy(() =>
  import("./containers/NucleusWorkflowsView").then((m) => ({ default: m.NucleusWorkflowsView })),
)
const DocsView = lazy(() =>
  import("./containers/DocsView").then((m) => ({ default: m.DocsView })),
)

/**
 * LMNT's pages, as plain RouteObjects mounted under the shell (@atomic/app-shell
 * `product.routes`). The shell adds the index → /data redirect, `/digital-twin`
 * (the core embed, persistent), `/linear`'s embed by hand below, `/admin` (core's
 * user-admin surface with View As), `/nucleus` (the full-page dock) and the
 * sign-in surfaces; everything here is LMNT's own.
 *
 * Why `/data`, `/tickets`, `/trackers`, `/allocation-engine` and the reporting
 * routes stay LMNT wrappers rather than the shell's `EmbedPage`: each hands its
 * adapter something the shell's generic page cannot — the route hash as the
 * master-data `tab` (/data#connections from the Nucleus dock switches the live
 * tab), `page="tickets"`, the tracker's `onNavigate` into our /reporting routes,
 * the allocation embed's `reviewerIdToken` (NUC-218), and the reporting embed's
 * drafts project (`?project=<company>-drafts`) + reset-to-home nonce. They are
 * candidate app-shell additions; until then they live here.
 */
export const PRODUCT_ROUTES: RouteObject[] = [
  // The Data Hub landing page is retired — /data (the master-data
  // embed) covers it, with source connections on /data#connections.
  // Old links and bookmarks land there. The mapping-folder and
  // source drill-downs below stay routable until their editors have
  // a home inside the embed.
  { path: "data-hub", element: <Navigate to="/data#connections" replace /> },
  {
    path: "data-hub/source/:name",
    element: (
      <LazyRoute>
        <DataSourceDetailView />
      </LazyRoute>
    ),
  },
  {
    path: "data-hub/:folder",
    element: (
      <LazyRoute>
        <MappingDetailView />
      </LazyRoute>
    ),
  },
  { path: "master-data", element: <Navigate to="/data" replace /> },
  {
    path: "data",
    element: (
      <LazyRoute>
        <MasterDataView />
      </LazyRoute>
    ),
  },
  {
    // The Linear embed, on the shell's generic embed page (registered in
    // main.tsx). Not at the shell's own `/tickets`: that path is LMNT's
    // all-areas ticket list below.
    path: "linear",
    element: <EmbedPage embed="linear" loadingLabel="Loading Linear…" />,
  },
  // Demand primary tab → the native pivot, reading core's demand
  // slice (static route wins over demand/:reportSlug below by route
  // ranking). The slugged reports stay on the reporting embed.
  {
    path: "demand",
    element: (
      <LazyRoute>
        <DemandReportingView />
      </LazyRoute>
    ),
  },
  {
    // Slugged demand reports keep serving the reporting embed pinned
    // to Sales Forecast so in-frame report nav stays under /demand,
    // mirroring reporting/:reportSlug?.
    path: "demand/:reportSlug",
    element: (
      <LazyRoute>
        <ReportingView
          reportSlug={DEMAND_REPORT_SLUG}
          basePath={DEMAND_BASE_PATH}
        />
      </LazyRoute>
    ),
  },
  {
    // Direct-nav only (no sidebar entry): every area's Linear tickets
    // (not just Master Data) — the masterdata embed opened on its
    // standalone tickets page, with a Category column + filter; company
    // bound by the embed token.
    path: "tickets",
    element: (
      <LazyRoute>
        <TicketsView />
      </LazyRoute>
    ),
  },
  {
    // Plan Tracker — the shared route every client app exposes at /trackers
    // (core deep-links it: ?type=&scenario=&upload_group=&chart=<json>).
    path: "trackers",
    element: (
      <LazyRoute>
        <TrackerView />
      </LazyRoute>
    ),
  },
  // Old spelling: keep every bookmark and link alive, query intact.
  { path: "tracker", element: <TrackerRedirect /> },
  {
    // Direct-nav only (no sidebar entry): the BOM Explorer, served whole by
    // atomic-core as an iframe embed over the DRAFT sourcing model
    // (item_source + sourcing_bom). ?item=<item_key> deep-links a root.
    path: "bom-explorer",
    element: (
      <LazyRoute>
        <BomExplorerView />
      </LazyRoute>
    ),
  },
  {
    // Direct-nav only (no sidebar entry): the Allocation Engine, served
    // whole by atomic-core as an iframe embed. The page — module
    // selector, precompiled inputs, the run, and its balance walk /
    // allocations / validations — lives there; we supply the route and
    // the token mint. Running the engine is gated to ROOT/ADMIN at that
    // mint, so the route needs no guard of its own.
    path: "allocation-engine",
    element: (
      <LazyRoute>
        <AllocationEngineView />
      </LazyRoute>
    ),
  },
  {
    // Direct-nav only (no sidebar entry): the master-data dependency
    // tree, rendered by @atomic/balance-walk-engine.
    path: "balance-walk",
    element: (
      <LazyRoute>
        <BalanceWalkView />
      </LazyRoute>
    ),
  },
  // The Replenishment page, at every path it mounts (containers/ReplenishmentView/pages.ts).
  // A splat, not `:tab?`: the page renders each view under descendant
  // <Routes> (a hidden view reads its own frozen location), which the
  // router only expects below a route that ends in `*`.
  ...REPLENISHMENT_PAGES.map((page) => ({
    path: `${page.basePath.slice(1)}/*`,
    element: (
      <LazyRoute>
        <ReplenishmentView {...page} />
      </LazyRoute>
    ),
  })),
  {
    // Optional :reportSlug — bare tab and deep links share one route
    // object so in-frame report navigation never remounts the iframe.
    // Optional :qfSlug carries a quick filter as a path segment
    // (/reporting/TimeSeries/SalesForecast), mirroring queryviz's own
    // canonical report paths.
    path: "reporting/:reportSlug?/:qfSlug?",
    element: (
      <LazyRoute>
        <ReportingView />
      </LazyRoute>
    ),
  },
  {
    path: "runs/history",
    element: (
      <LazyRoute>
        <RunHistoryView />
      </LazyRoute>
    ),
  },
  {
    path: "docs",
    element: (
      <LazyRoute>
        <DocsView />
      </LazyRoute>
    ),
  },
  ...(nucleusWorkflowsEnabled
    ? [
        {
          path: "workflows",
          element: (
            <LazyRoute>
              <NucleusWorkflowsView />
            </LazyRoute>
          ),
        },
      ]
    : []),
  // User administration moved to the shell's `/admin` (core's user-admin
  // surface, View As beside it); the old address keeps working.
  { path: "user-admin", element: <Navigate to="/admin" replace /> },
  // Catch-all: unknown URLs render a branded 404 inside the normal
  // app chrome. Listed before the shell's own catch-all (a redirect home),
  // so this one wins.
  { path: "*", element: <RouteErrorView /> },
]
